
import { useState, useEffect, useRef, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { BrandSetupData } from "@/components/brand-setup/BrandSetupTypes";
import { toast } from "sonner";

export const useBrandData = (brandId: string | null) => {
  const [brandData, setBrandData] = useState<BrandSetupData>({
    brandName: '',
    faviconAssetId: null,
    affiliateBrandId: null,
    keywords: [],
    seedKeywords: [],
    personas: [],
    prompts: [],
    competitors: [],
    ourPeople: [],
    targetedCountries: [],
    languages: [],
    description: '',
    website: '',
    seedHashtags: [],
    brandHashtags: [],
    indicativeTestimonials: [],
    currency: 'USD',
    slackChannel: '',
    blogRssUrls: [],
    importantPages: [],
    youtube: '',
    instagram: '',
    facebook: '',
    tiktok: '',
    x: '',
    linkedin: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const lastFetchTime = useRef<number>(0);
  const cacheDuration = 5 * 60 * 1000; // 5 minutes cache
  const isLoadingRef = useRef(false);
  const draftStateRef = useRef<BrandSetupData | null>(null);

  // CRITICAL FIX: Reduce aggressive auto-save to prevent data loss
  // Only save draft on explicit user action, not on every state change
  const setBrandDataWithDraft = useCallback((data: BrandSetupData) => {
    setBrandData(data);
    draftStateRef.current = data;
    
    // REMOVED: Automatic sessionStorage save that could overwrite DB data
    // Draft should only be saved on explicit save action
  }, [brandId]);

  // Load draft state from sessionStorage
  const loadDraftState = useCallback((brandId: string) => {
    const draftKey = `brandDraft_${brandId}`;
    const draftData = sessionStorage.getItem(draftKey);
    const draftTimestamp = sessionStorage.getItem(`${draftKey}_timestamp`);
    
    if (draftData && draftTimestamp) {
      const timeSinceDraft = Date.now() - parseInt(draftTimestamp);
      // Keep draft for up to 30 minutes of inactivity
      if (timeSinceDraft < 30 * 60 * 1000) {
        try {
          const parsedDraft = JSON.parse(draftData);
          console.log("Loading draft state from session:", parsedDraft);
          draftStateRef.current = parsedDraft;
          setBrandData(parsedDraft);
          setIsLoading(false); // Clear loading state
          return true;
        } catch (error) {
          console.error("Error parsing draft data:", error);
          sessionStorage.removeItem(draftKey);
          sessionStorage.removeItem(`${draftKey}_timestamp`);
        }
      } else {
        // Clean up expired draft
        sessionStorage.removeItem(draftKey);
        sessionStorage.removeItem(`${draftKey}_timestamp`);
      }
    }
    return false;
  }, []);

  // Check if we need to refetch based on cache
  const shouldRefetch = useCallback((brandId: string) => {
    const now = Date.now();
    const cacheKey = `brandData_${brandId}`;
    const cachedData = localStorage.getItem(cacheKey);
    const cacheTime = localStorage.getItem(`${cacheKey}_time`);
    
    if (!cachedData || !cacheTime) return true;
    
    const timeSinceCache = now - parseInt(cacheTime);
    return timeSinceCache > cacheDuration;
  }, [cacheDuration]);

  const loadBrandDataFromCache = useCallback((brandId: string) => {
    const cacheKey = `brandData_${brandId}`;
    const cachedData = localStorage.getItem(cacheKey);
    
    if (cachedData) {
      try {
        const parsedData = JSON.parse(cachedData);
        setBrandData(parsedData);
        draftStateRef.current = parsedData; // Sync draft with cache
        setIsLoading(false); // Clear loading state
        return true;
      } catch (error) {
        console.error("Error parsing cached brand data:", error);
        localStorage.removeItem(cacheKey);
        localStorage.removeItem(`${cacheKey}_time`);
      }
    }
    return false;
  }, []);

  useEffect(() => {
    const loadBrandData = async () => {
      if (!brandId) {
        console.log("No brand ID provided, resetting data");
        setBrandData({
          brandName: '',
          faviconAssetId: null,
          affiliateBrandId: null,
          keywords: [],
          seedKeywords: [],
          personas: [],
          prompts: [],
          competitors: [],
          ourPeople: [],
          targetedCountries: [],
          languages: [],
          description: '',
          website: '',
          seedHashtags: [],
          brandHashtags: [],
          indicativeTestimonials: [],
          currency: 'USD',
          slackChannel: '',
          blogRssUrls: [],
          importantPages: [],
          youtube: '',
          instagram: '',
          facebook: '',
          tiktok: '',
          x: '',
          linkedin: ''
        });
        setIsLoading(false);
        return;
      }

      // Prevent multiple simultaneous requests
      if (isLoadingRef.current) {
        console.log("Already loading brand data, skipping request");
        return;
      }

      // First priority: Check for draft state (unsaved changes)
      if (loadDraftState(brandId)) {
        console.log("Using draft state, not fetching fresh data");
        setIsLoading(false); // Ensure loading is cleared
        return;
      }

      // Second priority: Check cache
      if (!shouldRefetch(brandId)) {
        console.log("Using cached brand data, not refetching");
        loadBrandDataFromCache(brandId);
        setIsLoading(false); // Ensure loading is cleared
        return;
      }

      try {
        setIsLoading(true);
        isLoadingRef.current = true;

        // Load brand info including new fields
        const { data: brand, error: brandError } = await supabase
          .from('brands')
          .select('name, user_id, favicon_asset_id, affiliate_brand_id')
          .eq('id', brandId)
          .single();

        if (brandError) {
          console.error('Error loading brand:', brandError);
          toast.error('Failed to load brand information');
          return;
        }

        const { data: brandDataRecord, error: brandDataError } = await supabase
          .from('brand_data')
          .select('*')
          .eq('brand_id', brandId)
          .maybeSingle();

        if (brandDataError) {
          console.error('Brand data query error:', brandDataError);
          toast.error('Failed to load brand data');
          return;
        }

        // Also try a regular select to see if there are multiple records
        const { data: allRecords, error: allRecordsError } = await supabase
          .from('brand_data')
          .select('*')
          .eq('brand_id', brandId);

        // If no brand_data record exists, create one with defaults
        if (!brandDataRecord && (!allRecords || allRecords.length === 0)) {
          console.log('No brand_data record found, creating default record');
          
          const defaultBrandData = {
            brand_id: brandId,
            keywords: [],
            seed_keywords: [],
            personas: [],
            prompts: [],
            competitors: [],
            targeted_countries: [],
            description: '',
            website: ''
          };

          const { data: newBrandData, error: createError } = await supabase
            .from('brand_data')
            .insert(defaultBrandData)
            .select()
            .single();

          if (createError) {
            console.error('Error creating default brand_data:', createError);
            // Continue with empty data rather than failing completely
          } else {
            console.log('Created default brand_data record:', newBrandData);
          }
        }

        // Use either the single record or the first record from all records
        const dataRecord = brandDataRecord || (allRecords && allRecords.length > 0 ? allRecords[0] : null);

        // Parse targeted countries safely
        let targetedCountries: Array<{ code: number; name: string }> = [];
        if (dataRecord && dataRecord.targeted_countries) {
          try {
            const countries = Array.isArray(dataRecord.targeted_countries) 
              ? dataRecord.targeted_countries 
              : [];
            
            targetedCountries = countries
              .filter((country: any) => 
                country && 
                typeof country === 'object' && 
                typeof country.code === 'number' && 
                typeof country.name === 'string'
              )
              .map((country: any) => ({
                code: country.code,
                name: country.name
              }));
          } catch (error) {
            console.error('Error parsing targeted_countries:', error);
          }
        }

        // Parse languages
        let languages: Array<{ code: string; name: string }> = [];
        if (dataRecord?.languages) {
          try {
            const languagesData = Array.isArray(dataRecord.languages) 
              ? dataRecord.languages 
              : [];
            
            languages = languagesData
              .filter((language: any) => 
                language && 
                typeof language === 'object' && 
                typeof language.code === 'string' && 
                typeof language.name === 'string'
              )
              .map((language: any) => ({
                code: language.code,
                name: language.name
              }));
          } catch (error) {
            console.error('Error parsing languages:', error);
          }
        }

        // Parse our_people with backward compatibility
        let ourPeople: Array<{ name: string; aliases: string[] }> = [];
        if (dataRecord?.our_people) {
          try {
            const peopleData = Array.isArray(dataRecord.our_people) 
              ? dataRecord.our_people 
              : [];
            
            ourPeople = peopleData
              .filter((person: any) => person && typeof person === 'object' && person.name)
              .map((person: any) => ({
                name: person.name,
                aliases: Array.isArray(person.aliases) ? person.aliases : []
              }));
          } catch (error) {
            console.error('Error parsing our_people:', error);
          }
        }

        const finalBrandData = {
          brandName: brand.name,
          faviconAssetId: brand.favicon_asset_id || null,
          affiliateBrandId: brand.affiliate_brand_id || null,
          keywords: dataRecord?.keywords || [],
          seedKeywords: dataRecord?.seed_keywords || [],
          personas: dataRecord?.personas || [],
          prompts: dataRecord?.prompts || [],
          competitors: dataRecord?.competitors || [],
          ourPeople: ourPeople,
          targetedCountries: targetedCountries,
          languages: languages,
          description: dataRecord?.description || '',
          website: dataRecord?.website || '',
          seedHashtags: dataRecord?.seed_hashtags || [],
          brandHashtags: dataRecord?.brand_hashtags || [],
          indicativeTestimonials: dataRecord?.indicative_testimonials || [],
          currency: dataRecord?.currency || 'USD',
          slackChannel: dataRecord?.slack_channel || '',
          blogRssUrls: dataRecord?.blog_rss_urls || [],
          importantPages: (dataRecord?.important_pages as Array<{ label: string; url: string }>) || [],
          youtube: dataRecord?.youtube || '',
          instagram: dataRecord?.instagram || '',
          facebook: dataRecord?.facebook || '',
          tiktok: dataRecord?.tiktok || '',
          x: dataRecord?.x || '',
          linkedin: dataRecord?.linkedin || ''
        };
        
        setBrandData(finalBrandData);

        // CRITICAL FIX: Remove aggressive caching that causes data loss
        // Do NOT cache to localStorage automatically - only cache on user action
        // Clear any existing cache to prevent stale data issues
        const cacheKey = `brandData_${brandId}`;
        localStorage.removeItem(cacheKey);
        localStorage.removeItem(`${cacheKey}_time`);
        lastFetchTime.current = Date.now();

      } catch (error) {
        console.error('Unexpected error loading brand data:', error);
        toast.error('Failed to load brand data');
      } finally {
        setIsLoading(false);
        isLoadingRef.current = false;
      }
    };

    loadBrandData();
  }, [brandId, shouldRefetch, loadBrandDataFromCache, loadDraftState]);

  // CRITICAL FIX: Remove automatic refetch on visibility change to prevent stale cache loading
  // This was causing data loss by loading old cached/draft data over fresh DB data
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && brandId) {
        console.log("Page became visible - NOT auto-refetching to prevent cache issues");
        // REMOVED: Automatic refetch that could load stale data
        // User should manually refresh if they want fresh data
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [brandId]);

  // Auto-save draft state every few seconds
  useEffect(() => {
    if (!brandId || !draftStateRef.current) return;

    const autoSaveDraft = () => {
      if (draftStateRef.current) {
        const draftKey = `brandDraft_${brandId}`;
        sessionStorage.setItem(draftKey, JSON.stringify(draftStateRef.current));
        sessionStorage.setItem(`${draftKey}_timestamp`, Date.now().toString());
      }
    };

    const interval = setInterval(autoSaveDraft, 3000); // Auto-save every 3 seconds
    return () => clearInterval(interval);
  }, [brandId]);

  // Force refresh function that clears cache and fetches fresh data
  const forceRefresh = useCallback(async () => {
    if (!brandId) return;
    
    console.log("Force refreshing brand data...");
    
    // Clear both draft and cache
    const draftKey = `brandDraft_${brandId}`;
    const cacheKey = `brandData_${brandId}`;
    sessionStorage.removeItem(draftKey);
    sessionStorage.removeItem(`${draftKey}_timestamp`);
    localStorage.removeItem(cacheKey);
    localStorage.removeItem(`${cacheKey}_time`);
    
    // Reset last fetch time to force a new fetch
    lastFetchTime.current = 0;
    
    // Trigger a fresh load
    setIsLoading(true);
    isLoadingRef.current = false; // Allow new request
    
    // Wait a tiny bit for state to clear
    await new Promise(resolve => setTimeout(resolve, 10));
    
    // Trigger the fetch by updating a dependency
    const loadBrandData = async () => {
      if (isLoadingRef.current) return;
      
      try {
        isLoadingRef.current = true;
        console.log("Fetching fresh brand data for brand ID:", brandId);

        const { data: brand, error: brandError } = await supabase
          .from('brands')
          .select('name, user_id, favicon_asset_id, affiliate_brand_id')
          .eq('id', brandId)
          .single();

        if (brandError) {
          console.error('Error loading brand:', brandError);
          toast.error('Failed to load brand information');
          return;
        }

        const { data: brandDataRecord, error: brandDataError } = await supabase
          .from('brand_data')
          .select('*')
          .eq('brand_id', brandId)
          .maybeSingle();

        if (brandDataError) {
          console.error('Brand data query error:', brandDataError);
          toast.error('Failed to load brand data');
          return;
        }

        const freshData: BrandSetupData = {
          brandName: brand.name || '',
          faviconAssetId: brand.favicon_asset_id || null,
          affiliateBrandId: brand.affiliate_brand_id || null,
          keywords: (brandDataRecord?.keywords as string[]) || [],
          seedKeywords: (brandDataRecord?.seed_keywords as string[]) || [],
          personas: (brandDataRecord?.personas as string[]) || [],
          prompts: (brandDataRecord?.prompts as string[]) || [],
          competitors: (brandDataRecord?.competitors as string[]) || [],
          ourPeople: (brandDataRecord?.our_people as Array<{ name: string; aliases: string[] }>) || [],
          targetedCountries: (brandDataRecord?.targeted_countries as Array<{ code: number; name: string }>) || [],
          languages: (brandDataRecord?.languages as Array<{ code: string; name: string }>) || [],
          description: brandDataRecord?.description || '',
          website: brandDataRecord?.website || '',
          seedHashtags: (brandDataRecord?.seed_hashtags as string[]) || [],
          brandHashtags: (brandDataRecord?.brand_hashtags as string[]) || [],
          indicativeTestimonials: (brandDataRecord?.indicative_testimonials as string[]) || [],
          currency: brandDataRecord?.currency || 'USD',
          slackChannel: brandDataRecord?.slack_channel || '',
          blogRssUrls: (brandDataRecord?.blog_rss_urls as string[]) || [],
          importantPages: (brandDataRecord?.important_pages as Array<{ label: string; url: string }>) || [],
          youtube: brandDataRecord?.youtube || '',
          instagram: brandDataRecord?.instagram || '',
          facebook: brandDataRecord?.facebook || '',
          tiktok: brandDataRecord?.tiktok || '',
          x: brandDataRecord?.x || '',
          linkedin: brandDataRecord?.linkedin || ''
        };

        console.log("Fresh data loaded:", freshData);

        // Update state
        setBrandData(freshData);
        draftStateRef.current = freshData;

        // CRITICAL FIX: Do NOT cache to localStorage/sessionStorage
        // This prevents stale data from overwriting fresh DB data
        const cacheKey = `brandData_${brandId}`;
        const draftKey = `brandDraft_${brandId}`;
        localStorage.removeItem(cacheKey);
        localStorage.removeItem(`${cacheKey}_time`);
        sessionStorage.removeItem(draftKey);
        sessionStorage.removeItem(`${draftKey}_timestamp`);

        lastFetchTime.current = Date.now();
        toast.success('Data refreshed successfully');
      } catch (error) {
        console.error('Error refreshing brand data:', error);
        toast.error('Failed to refresh data');
      } finally {
        setIsLoading(false);
        isLoadingRef.current = false;
      }
    };

    await loadBrandData();
  }, [brandId]);

  return {
    brandData,
    setBrandData: setBrandDataWithDraft,
    isLoading,
    forceRefresh
  };
};
